import { AccountRunStatus } from '@hit-game/shared/enums';
import type { IAccountDataResponse } from '@hit-game/shared/types';
import {
  History,
  Info,
  LogIn,
  LogOut,
  Play,
  QrCode,
  RefreshCw,
  ScrollText,
  Square,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { ActionButton } from './ActionButton';
import type { AccountCardActions as AccountCardCallbacks } from './AccountCard';

interface AccountCardActionsProps extends AccountCardCallbacks {
  account: IAccountDataResponse;
  /**
   * Hiện nút đăng nhập/đăng xuất của RIÊNG tài khoản này.
   *
   * Tắt khi thẻ nằm trong nhóm: ở đó việc này làm ở cấp nhóm, để lộ nút lẻ là
   * người dùng đăng xuất một vế của cặp đối ứng mà vế kia vẫn đang đánh. Xoá thì
   * KHÔNG tắt — gỡ hẳn một tài khoản là việc chỉ làm được ở thẻ của nó.
   */
  showSoloActions: boolean;
  /**
   * Hàng điều khiển Máy bay (Aviator). Bỏ trống = không hiện — thẻ trong nhóm và
   * thẻ ở tab TX/XĐ không có việc gì với Aviator.
   *
   * `running` đọc từ phiên Aviator RIÊNG chứ không dùng `account.status`: cờ đó
   * bật cho cả nhóm lẫn Aviator, nên dựa vào nó thì một tài khoản đang chạy nhóm
   * sẽ hiện như đang chạy Máy bay.
   */
  aviator?: AviatorCardControls;
}

export interface AviatorCardControls {
  configured: boolean;
  running: boolean;
  onStart: (id: string) => Promise<void>;
  onStop: (id: string) => Promise<void>;
  onConfig: (account: IAccountDataResponse) => void;
}

export function AccountCardActions({
  account,
  showSoloActions,
  aviator,
  onLogin,
  onLogout,
  onRefreshBalance,
  onShowBetLogs,
  onShowTransactions,
  onShowDeposit,
  onShowInfo,
  onDelete,
}: AccountCardActionsProps) {
  const [busy, setBusy] = useState<'login' | 'logout' | 'balance' | 'play' | 'stop' | null>(null);

  const id = String(account._id);
  const loggedIn = account.isLoggedIn === true;
  const running = account.status === AccountRunStatus.RUNNING;

  const run = async (
    kind: 'login' | 'logout' | 'balance' | 'play' | 'stop',
    task: () => Promise<void>,
  ) => {
    setBusy(kind);
    try {
      await task();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border-line space-y-1.5 border-t p-3">
      {showSoloActions && (
        <div className="grid grid-cols-2 gap-1.5">
          <ActionButton
            onClick={() => {
              void run('login', () => onLogin(id));
            }}
            disabled={loggedIn || busy !== null}
            color="blue"
          >
            {busy === 'login' ? (
              <span className="border-accent h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
            ) : (
              <LogIn className="h-3.5 w-3.5" />
            )}
            Đăng nhập
          </ActionButton>
          <ActionButton
            onClick={() => {
              void run('logout', () => onLogout(id));
            }}
            // Chặn đăng xuất khi engine đang đánh: mất phiên giữa ván là mất tiền cược.
            disabled={!loggedIn || running || busy !== null}
          >
            {busy === 'logout' ? (
              <span className="border-ink-dim h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
            ) : (
              <LogOut className="h-3.5 w-3.5" />
            )}
            Đăng xuất
          </ActionButton>
        </div>
      )}

      {aviator && (
        <div className="grid grid-cols-2 gap-1.5">
          <ActionButton
            onClick={() => {
              void run('play', () => aviator.onStart(id));
            }}
            // Chưa cấu hình thì server cũng từ chối — chặn ở đây để thấy LÝ DO
            // trước khi bấm, thay vì bấm rồi mới nhận lỗi.
            disabled={!aviator.configured || !loggedIn || aviator.running || busy !== null}
            color="green"
            title={
              !aviator.configured
                ? 'Chưa cấu hình Máy bay — bấm bánh răng để đặt tiền cược và mức tự dừng'
                : !loggedIn
                  ? 'Tài khoản chưa đăng nhập'
                  : 'Bắt đầu tự đặt cược Máy bay'
            }
          >
            {busy === 'play' ? (
              <span className="border-success h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            Start
          </ActionButton>
          <ActionButton
            onClick={() => {
              void run('stop', () => aviator.onStop(id));
            }}
            disabled={!aviator.running || busy !== null}
            color="red"
            title={aviator.running ? 'Dừng đặt cược Máy bay' : 'Máy bay chưa chạy'}
          >
            {busy === 'stop' ? (
              <span className="border-danger h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
            ) : (
              <Square className="h-3.5 w-3.5" />
            )}
            Stop
          </ActionButton>
        </div>
      )}

      <div className="grid grid-cols-3 gap-1.5">
        {/* Cả hàng: mật khẩu và proxy là thứ hay phải chép ra nhất, đừng bắt dò
            trong ô nút bé. */}
        <div className="col-span-3">
          <ActionButton
            onClick={() => {
              onShowInfo(account);
            }}
          >
            <Info className="h-3.5 w-3.5" />
            Thông tin tài khoản
          </ActionButton>
        </div>

        <ActionButton
          onClick={() => {
            onShowDeposit(account);
          }}
          disabled={!loggedIn}
          color="purple"
        >
          <QrCode className="h-3.5 w-3.5" />
          QR Nạp
        </ActionButton>
        <ActionButton
          onClick={() => {
            void run('balance', () => onRefreshBalance(id));
          }}
          disabled={!loggedIn || busy !== null}
          color="cyan"
        >
          {busy === 'balance' ? (
            <span className="border-action-cyan h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Số dư
        </ActionButton>
        <ActionButton
          onClick={() => {
            onShowBetLogs(account);
          }}
          disabled={!loggedIn}
          color="blue"
        >
          <ScrollText className="h-3.5 w-3.5" />
          Bet Log
        </ActionButton>

        {/* Hàng cuối chia ĐÔI: để chúng nằm trong lưới 3 cột thì hai nút co lại
            bằng 2/3 hàng trên và chừa một ô trống lệch hẳn ra. */}
        <div className="col-span-3 grid grid-cols-2 gap-1.5">
          <ActionButton
            onClick={() => {
              onShowTransactions(account);
            }}
            disabled={!loggedIn}
            color="teal"
          >
            <History className="h-3.5 w-3.5" />
            LS Nạp/Rút
          </ActionButton>

          <ActionButton
            onClick={() => {
              onDelete(account);
            }}
            // Đang đánh mà xoá là bỏ lại lệnh treo bên nhà cái, không ai gỡ được nữa.
            disabled={running}
            color="red"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Xoá
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
